import { useEffect, useRef, useState } from 'react';
import { api, photoSrc } from '../api';
import { SHOWS_LABEL, SHOWS_PLAYED, type BandHistoryEntry, type Photo, type Profile, type ShowsPlayed, type UserLink } from '../types';
import { BandPicker, type PickedBand } from '../components/BandPicker';
import { BirthdatePicker } from '../components/BirthdatePicker';
import { BioEditor } from '../components/BioEditor';
import { LocationPicker } from '../components/LocationPicker';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { TagPicker } from '../components/TagPicker';
import { VideoEmbed } from '../components/VideoEmbed';
import { prepareImage } from '../media';
import { videoEmbedUrl } from '../video';
import { normalizeUrl } from '../url';
import { BandNameInput } from '../components/BandNameInput';
import { SaveStatusDot, useAutoSave } from '../hooks/useAutoSave';
import { useTiledBackground } from '../hooks/useTiledBackground';

/**
 * Profile editor. Every card auto-saves a moment after you stop editing; there
 * are no Save buttons. Each card owns its own state so a slow band hydration
 * never blocks the rest.
 */
export function ProfilePage() {
  const [p, setP] = useState<Profile | null>(null);
  const [presets, setPresets] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.me().then(setP).catch((e) => setError(e.message));
    api.instruments().then((r) => setPresets(r.instruments)).catch(() => {});
  }, []);

  useTiledBackground(p?.backgroundUrl);

  if (error) return <div className="error">{error}</div>;
  if (!p) return <div className="spinner">Loading…</div>;

  return (
    <div className="stack">
      <PhotosCard p={p} onSaved={setP} />
      <BasicsCard p={p} onSaved={setP} />
      <TagCard title="Instruments I play" value={p.instruments} presets={presets} suggest={(q) => api.suggestInstruments(q).then((r) => r.results)}
        placeholder="Enter your instrument or select from below" max={20}
        save={(v) => api.setInstruments(v)} />
      <TagCard title="Genres I like" value={p.genres} suggest={(q) => api.suggestGenres(q).then((r) => r.results)}
        placeholder="Enter a genre" max={30} save={(v) => api.setGenres(v)} />
      <WantsCard p={p} presets={presets} />
      <BandsCard p={p} onSaved={setP} />
      <BandEntriesCard p={p} onSaved={setP} current />
      <BandEntriesCard p={p} onSaved={setP} current={false} />
      <LinksCard p={p} onSaved={setP} />
      <BackgroundCard p={p} onSaved={setP} />
    </div>
  );
}

function CardHead({ title, status, error }: { title: string; status: ReturnType<typeof useAutoSave>['status']; error: string | null }) {
  return <div className="card-head"><h2>{title}</h2><SaveStatusDot status={status} error={error} /></div>;
}

// --- Photos (up to 6; first is the avatar; tap to open) ------------------------------------------------

function PhotosCard({ p, onSaved }: { p: Profile; onSaved: (p: Profile) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<Photo | null>(null);
  const apply = (photos: Profile['photos']) => onSaved({ ...p, photos, photoUrl: photos[0]?.url ?? null });

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setErr(null);
    try {
      let photos = p.photos;
      for (const f of Array.from(files).slice(0, 6 - p.photos.length)) photos = (await api.uploadPhoto((await prepareImage(f)).file)).photos;
      apply(photos);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  return (
    <div className="card">
      <h2>Photos</h2>
      <div className="gallery-edit">
        {p.photos.map((ph, i) => (
          <button type="button" className="slot clickable" key={ph.id} onClick={() => setOpen(ph)} aria-label="Open photo">
            <img src={photoSrc(ph.url)!} alt="" />
            {i === 0 && <span className="badge">MAIN</span>}
          </button>
        ))}
        {p.photos.length < 6 && <button type="button" className="slot" onClick={() => fileRef.current?.click()} disabled={busy}>{busy ? '…' : '+ Add'}</button>}
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => upload(e.target.files)} />
      {err && <div className="error">{err}</div>}
      {open && (
        <PhotoLightbox photo={open} onClose={() => setOpen(null)}
          onDelete={async (id) => apply((await api.deletePhoto(id)).photos)}
          onReplace={async (id, file) => apply((await api.replacePhoto(id, file)).photos)} />
      )}
    </div>
  );
}

// --- Basics ---------------------------------------------------------------------------------------------

function BasicsCard({ p, onSaved }: { p: Profile; onSaved: (p: Profile) => void }) {
  const [v, setV] = useState({
    displayName: p.displayName, birthdate: p.birthdate, ageHidden: p.ageHidden, looking: p.looking,
    showsPlayed: (p.showsPlayed ?? '') as ShowsPlayed | '', bio: p.bio ?? '', city: p.city, lat: p.lat, lng: p.lng,
  });
  const { status, error } = useAutoSave(v, async (x) => {
    if (!x.displayName.trim()) throw new Error('Name is required');
    onSaved(await api.updateMe({ displayName: x.displayName.trim(), birthdate: x.birthdate, ageHidden: x.ageHidden, looking: x.looking, showsPlayed: x.showsPlayed || null, bio: x.bio || null, city: x.city, lat: x.lat, lng: x.lng }));
  });
  const set = (patch: Partial<typeof v>) => setV({ ...v, ...patch });
  return (
    <div className="card">
      <CardHead title="About you" status={status} error={error} />
      <label className="field"><span>Name</span><input type="text" value={v.displayName} onChange={(e) => set({ displayName: e.target.value })} maxLength={40} /></label>
      <div className="field"><span className="muted small">Birthday {p.age !== null ? `(age ${p.age})` : ''}</span><BirthdatePicker value={v.birthdate} onChange={(b) => set({ birthdate: b })} /></div>
      <label className="toggle" style={{ marginBottom: 12 }}>
        <input type="checkbox" checked={v.ageHidden} onChange={(e) => set({ ageHidden: e.target.checked })} />
        <span>Hide my age on my public profile</span>
      </label>
      <label className="toggle" style={{ marginBottom: 12 }}>
        <input type="checkbox" checked={v.looking} onChange={(e) => set({ looking: e.target.checked })} />
        <span>I'm currently looking for a band</span>
      </label>
      <label className="field"><span>Number of shows played</span>
        <select value={v.showsPlayed} onChange={(e) => set({ showsPlayed: e.target.value as ShowsPlayed | '' })}>
          <option value="">Prefer not to say</option>
          {SHOWS_PLAYED.map((s) => <option key={s} value={s}>{SHOWS_LABEL[s]}</option>)}
        </select>
      </label>
      <div className="field"><span className="muted small">Bio</span><BioEditor value={v.bio} onChange={(bio) => set({ bio })} /></div>
      <div className="field"><span className="muted small">City</span><LocationPicker value={{ city: v.city, lat: v.lat, lng: v.lng }} onChange={(l) => set(l)} /></div>
    </div>
  );
}

// --- Generic tag card (instruments, genres) ----------------------------------------------------------------

function TagCard({ title, value, presets, suggest, placeholder, max, save }: {
  title: string; value: string[]; presets?: string[]; suggest: (q: string) => Promise<string[]>; placeholder: string; max: number;
  save: (v: string[]) => Promise<unknown>;
}) {
  const [v, setV] = useState(value);
  const { status, error } = useAutoSave(v, save);
  return (
    <div className="card">
      <CardHead title={title} status={status} error={error} />
      <TagPicker value={v} onChange={setV} suggest={suggest} presets={presets} placeholder={placeholder} max={max} />
    </div>
  );
}

// --- I'm looking for -----------------------------------------------------------------------------------------

function WantsCard({ p, presets }: { p: Profile; presets: string[] }) {
  const [v, setV] = useState(p.wants);
  const { status, error } = useAutoSave(v, (w) => api.setWants(w));
  return (
    <div className="card stack">
      <CardHead title="I'm looking for" status={status} error={error} />
      <div>
        <div className="muted small" style={{ marginBottom: 6 }}>Instruments</div>
        <TagPicker value={v.instruments} onChange={(instruments) => setV({ ...v, instruments })} suggest={(q) => api.suggestInstruments(q).then((r) => r.results)} presets={presets} placeholder="Enter an instrument or select from below" max={20} />
      </div>
      <div>
        <div className="muted small" style={{ marginBottom: 6 }}>Genres</div>
        <TagPicker value={v.genres} onChange={(genres) => setV({ ...v, genres })} suggest={(q) => api.suggestGenres(q).then((r) => r.results)} placeholder="Enter a genre" max={20} />
      </div>
    </div>
  );
}

// --- Bands I like ------------------------------------------------------------------------------------------------

function BandsCard({ p, onSaved }: { p: Profile; onSaved: (p: Profile) => void }) {
  const [v, setV] = useState<PickedBand[]>(p.bands.map((b) => ({ ...b, top: b.topRank !== null })));
  // Longer debounce: saving hydrates new bands from MusicBrainz (~1 req/s), so let the user finish picking.
  const { status, error } = useAutoSave(v.map((b) => ({ mbid: b.mbid, top: b.top })), async (list) => {
    const np = await api.setBands(list);
    onSaved(np);
    setV(np.bands.map((b) => ({ ...b, top: b.topRank !== null })));
  }, 1500);
  return (
    <div className="card">
      <CardHead title="Bands I like" status={status} error={error} />
      <BandPicker value={v} onChange={setV} />
    </div>
  );
}

// --- Current band / past bands (one list, two cards) ---------------------------------------------------------------

const emptyEntry = (current: boolean): BandHistoryEntry => ({ name: '', role: null, url: null, isCurrent: current, bandMbid: null, imageUrl: null });

function BandEntriesCard({ p, onSaved, current }: { p: Profile; onSaved: (p: Profile) => void; current: boolean }) {
  const [v, setV] = useState<BandHistoryEntry[]>(p.bandHistory.filter((e) => e.isCurrent === current));
  const others = useRef(p.bandHistory.filter((e) => e.isCurrent !== current));
  useEffect(() => { others.current = p.bandHistory.filter((e) => e.isCurrent !== current); }, [p.bandHistory, current]);
  // Only complete entries are persisted; a half-typed row just waits. URLs are normalized (scheme optional).
  const complete = v.filter((e) => e.name.trim() && (!e.url || normalizeUrl(e.url))).map((e) => ({ ...e, url: e.url ? normalizeUrl(e.url) : null }));
  const { status, error } = useAutoSave(complete, (list) => api.setBandHistory([...others.current, ...list]).then(onSaved));
  const upd = (i: number, patch: Partial<BandHistoryEntry>) => setV(v.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  return (
    <div className="card">
      <CardHead title={current ? 'Current band' : "Bands I've played in"} status={status} error={error} />
      <div className="stack">
        {v.map((e, i) => (
          <div key={i} className="stack" style={{ background: 'var(--panel-2)', borderRadius: 10, padding: 10 }}>
            <BandNameInput name={e.name} bandMbid={e.bandMbid} imageUrl={e.imageUrl} onChange={(b) => upd(i, b)} />
            <input type="text" placeholder="Your role (drums, vocals…)" value={e.role ?? ''} onChange={(ev) => upd(i, { role: ev.target.value || null })} maxLength={80} />
            <input type="text" inputMode="url" autoCapitalize="none" placeholder="Link — bandcamp.com/yourband, Instagram, YouTube…" value={e.url ?? ''} onChange={(ev) => upd(i, { url: ev.target.value || null })} />
            {e.url && videoEmbedUrl(e.url) && <VideoEmbed url={e.url} title={e.name || 'video'} />}
            <div className="row between">
              <button className="btn ghost sm" onClick={() => upd(i, { isCurrent: !e.isCurrent })}>{e.isCurrent ? 'Move to past bands' : 'Mark as current'}</button>
              <button className="btn ghost sm" onClick={() => setV(v.filter((_, j) => j !== i))}>Remove</button>
            </div>
          </div>
        ))}
        <button className="btn secondary sm" onClick={() => setV([...v, emptyEntry(current)])}>+ Add a band</button>
      </div>
    </div>
  );
}

// --- Links (video links get an inline player) --------------------------------------------------------------------------

function LinksCard({ p, onSaved }: { p: Profile; onSaved: (p: Profile) => void }) {
  const [v, setV] = useState<UserLink[]>(p.links);
  const complete = v.filter((l) => l.label.trim() && normalizeUrl(l.url)).map((l) => ({ ...l, url: normalizeUrl(l.url)! }));
  const { status, error } = useAutoSave(complete, (list) => api.setLinks(list).then(onSaved));
  const upd = (i: number, patch: Partial<UserLink>) => setV(v.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <div className="card">
      <CardHead title="Links & videos" status={status} error={error} />
      <div className="stack">
        {v.map((l, i) => (
          <div key={i} className="stack">
            <div className="row">
              <input type="text" placeholder="Label" value={l.label} onChange={(e) => upd(i, { label: e.target.value })} maxLength={40} style={{ flex: '0 0 35%' }} />
              <input type="text" inputMode="url" autoCapitalize="none" placeholder="yourband.bandcamp.com" value={l.url} onChange={(e) => upd(i, { url: e.target.value })} />
              <button className="icon-btn" onClick={() => setV(v.filter((_, j) => j !== i))} aria-label="Remove">✕</button>
            </div>
            {videoEmbedUrl(l.url) && <VideoEmbed url={l.url} title={l.label || 'video'} />}
          </div>
        ))}
        <button className="btn secondary sm" onClick={() => setV([...v, { label: '', url: '' }])}>+ Add a link</button>
      </div>
    </div>
  );
}

// --- Tiled background -----------------------------------------------------------------------------------------------------

function BackgroundCard({ p, onSaved }: { p: Profile; onSaved: (p: Profile) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const upload = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    setErr(null);
    try { const r = await api.uploadBackground(f); onSaved({ ...p, backgroundUrl: r.backgroundUrl }); } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const bg = photoSrc(p.backgroundUrl);
  return (
    <div className="card">
      <h2>Profile background</h2>
      <div className="row">
        <button className="btn secondary sm" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? 'Uploading…' : bg ? 'Change image' : 'Upload image'}</button>
        {bg && <button className="btn ghost sm" onClick={() => api.deleteBackground().then(() => onSaved({ ...p, backgroundUrl: null }))}>Remove</button>}
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => upload(e.target.files?.[0])} />
      {err && <div className="error">{err}</div>}
    </div>
  );
}
